import {
  type CompareResourceLevelUpProps,
  type ShortStorageItemProps,
} from '@common/types/components';
import {
  type AvailableQuarry,
  type BuildRequirementsType,
  type QuarriesLevelListType,
  type UserQuarry,
} from '@common/types/quarryType';
import { type ResourceTypeText } from '@common/types/resource';
import { type RootState } from '@redux/store';
import { createSelector } from '@reduxjs/toolkit';
import { calculateBonusTgem, type ResourceObject } from '@shared-libs/index';

import { resourceList } from '@/common/constants';
import { getProductionCycleTime } from '@/common/lib/getProductionCycleTime';
import { type BoostItemType } from '@/common/types/factoryType';

import {
  calculateTotalBoost,
  checkIfBoostTimersEnd,
  checkIfFreeBoostAvailable,
  createRequirementsList,
  generateActiveBoostList,
  getActiveBoost,
  getMaxExpiresTimeByPlant,
  mapBoostForCalcMining,
  selectAdsBoost,
  selectBoostX5,
  selectBoostX10,
  selectFreeBoost,
  selectPaidBoost,
  selectVipBoost,
} from './lib';

const selectQuarryNextToBuild = (
  state: RootState,
): AvailableQuarry | undefined => state.quarry.quarries?.nextToBuild;

const selectNextToBuildCreation = (
  state: RootState,
): BuildRequirementsType | undefined =>
  state.quarry.quarries?.nextToBuild.creationRequirements;

const selectQuarriesList = (state: RootState): UserQuarry[] | undefined =>
  state.quarry.quarries?.userQuarries;

const selectQuarriesBoost = (
  state: RootState,
): BoostItemType[] | undefined | null => state.quarry.quarries?.boosts;

const selectAllResourcesForClaim = createSelector(
  [selectQuarriesList],
  (userQuarries) => {
    if (userQuarries) {
      if (!userQuarries.length) {
        return [];
      }

      const storageMap = new Map<string, ShortStorageItemProps>([]);

      resourceList.forEach((resourceName) => {
        storageMap.set(resourceName, { kind: resourceName });
      });

      userQuarries.forEach((quarry) => {
        storageMap.set(quarry.name, {
          kind: quarry.name as ResourceTypeText,
          storage: {
            stock: quarry.localStock as number,
            limit: quarry.storageCapacity as number,
          },
        });
      });

      return [...storageMap.values()];
    }
  },
);

const selectQuarryByIdOrName = createSelector(
  selectQuarriesList,
  (_: RootState, key: string | undefined): string | undefined => key,
  (userQuarries, key) =>
    userQuarries && key
      ? userQuarries.find(({ name, id }) => name === key || id === key)
      : undefined,
);

const selectQuarryById = createSelector(
  selectQuarriesList,
  (_: RootState, resourceId?: string): string | undefined => resourceId,
  (userQuarries, resourceId) =>
    userQuarries && resourceId
      ? userQuarries.find(({ id }) => id === resourceId)
      : undefined,
);

const selectQuarryBuildRequirementsByResourceId = createSelector(
  selectNextToBuildCreation,
  createRequirementsList,
);

const checkIfResourceAvailableToClaim = createSelector(
  selectQuarriesList,
  (quarries): boolean => {
    if (quarries) {
      return quarries.some(
        (quarry) => quarry.localStock > quarry.storageCapacity / 2,
      );
    } else {
      return false;
    }
  },
);

const selectQuarryLevelUpData = createSelector(
  selectQuarriesList,
  (
    _: RootState,
    args: { resourceId?: string; withNext: boolean },
  ): { resourceId?: string; withNext: boolean } => args,
  (quarries, args) => {
    if (quarries && args.resourceId) {
      const quarry = quarries.find((quarry) => quarry.id === args.resourceId);

      if (quarry) {
        const miningCompare: CompareResourceLevelUpProps = {
          resource: quarry.name,
          compaireKind: 'mining',
          now: quarry.productionRateOriginal,
          next: args.withNext
            ? quarry.nextLevelRequirements?.productionRate || 0
            : undefined,
        };
        const storageCompare: CompareResourceLevelUpProps = {
          resource: quarry.name,
          compaireKind: 'loan',
          now: getProductionCycleTime(
            quarry.storageCapacityOriginal,
            quarry.productionRateOriginal,
          ),
          next: args.withNext
            ? getProductionCycleTime(
                quarry.nextLevelRequirements?.storageCapacity || 0,
                quarry.nextLevelRequirements?.productionRate || 0,
              )
            : undefined,
        };
        const tapCompare: CompareResourceLevelUpProps = {
          resource: quarry.name,
          compaireKind: 'tap',
          now: quarry.resourcesPerTap,
          next: args.withNext
            ? quarry.nextLevelRequirements?.resourcesPerTap || 0
            : undefined,
        };

        return [miningCompare, storageCompare, tapCompare];
      }
    }
  },
);

const selectQuarriesLevels = createSelector(selectQuarriesList, (quarries) => {
  if (quarries) {
    const quarriesLevelList: QuarriesLevelListType = {};

    quarries.forEach((quarry) => {
      quarriesLevelList[quarry.name] = quarry.level;
    });

    return quarriesLevelList;
  }
});

const selectQuarriesAsObject = createSelector(
  selectQuarriesList,
  (quarries) => {
    if (quarries) {
      const quarriesObject: Partial<Record<ResourceTypeText, UserQuarry>> = {};

      quarries.forEach((quarry) => {
        quarriesObject[quarry.name] = quarry;
      });

      return quarriesObject;
    }
  },
);

const selectTotalQuarryBoostById = createSelector(
  selectQuarriesBoost,
  (_: RootState, plantId: string): string => plantId,
  calculateTotalBoost,
);

const selectActiveQuarriesBoosts = createSelector(
  selectQuarriesBoost,
  generateActiveBoostList,
);

const getMaxExpiresTimeForQuarryBoosts = createSelector(
  selectQuarriesBoost,
  (_: RootState, plantId: string): string => plantId,
  getMaxExpiresTimeByPlant,
);

const selectBoostForQuarry = createSelector(
  selectQuarriesBoost,
  (_: RootState, plantId: string | undefined): string | undefined => plantId,
  mapBoostForCalcMining,
);

const selectFreeBoostForQuarry = createSelector(
  selectQuarriesBoost,
  (_: RootState, plantId?: string): string | undefined => plantId,
  selectFreeBoost,
);

const selectAdsBoostForQuarry = createSelector(
  selectQuarriesBoost,
  (_: RootState, plantId?: string): string | undefined => plantId,
  selectAdsBoost,
);

const selectVipBoostForQuarry = createSelector(
  selectQuarriesBoost,
  (_: RootState, plantId?: string): string | undefined => plantId,
  selectVipBoost,
);

const selectBoostX5ForQuarry = createSelector(
  selectQuarriesBoost,
  (_: RootState, plantId?: string): string | undefined => plantId,
  selectBoostX5,
);
const selectBoostX10ForQuarry = createSelector(
  selectQuarriesBoost,
  (_: RootState, plantId?: string): string | undefined => plantId,
  selectBoostX10,
);

const selectPaidBoostForQuarry = createSelector(
  selectQuarriesBoost,
  (_: RootState, plantId?: string): string | undefined => plantId,
  selectPaidBoost,
);

const selectActiveBoostForQuarry = createSelector(
  selectQuarriesBoost,
  getActiveBoost,
);

const boostQuarryTimersEnd = createSelector(
  selectQuarriesBoost,
  checkIfBoostTimersEnd,
);

const quarryFreeBoostAvailable = createSelector(
  selectQuarriesBoost,
  checkIfFreeBoostAvailable,
);

const collectBonus = createSelector(selectQuarriesList, (quarries) => {
  if (quarries) {
    const objectArr: ResourceObject[] = quarries.map((quarry) => ({
      productionPerHour: quarry.productionRate,
      storedResources: quarry.localStock,
    }));

    return calculateBonusTgem(objectArr);
  }
});

export {
  boostQuarryTimersEnd,
  checkIfResourceAvailableToClaim,
  collectBonus,
  getMaxExpiresTimeForQuarryBoosts,
  quarryFreeBoostAvailable,
  selectActiveBoostForQuarry,
  selectActiveQuarriesBoosts,
  selectAdsBoostForQuarry,
  selectAllResourcesForClaim,
  selectBoostForQuarry,
  selectBoostX5ForQuarry,
  selectBoostX10ForQuarry,
  selectFreeBoostForQuarry,
  selectPaidBoostForQuarry,
  selectQuarriesAsObject,
  selectQuarriesLevels,
  selectQuarriesList,
  selectQuarryBuildRequirementsByResourceId,
  selectQuarryById,
  selectQuarryByIdOrName,
  selectQuarryLevelUpData,
  selectQuarryNextToBuild,
  selectTotalQuarryBoostById,
  selectVipBoostForQuarry,
};
